import { ref, onUnmounted } from 'vue'
import type { PluginListenerHandle } from '@capacitor/core'
import { Keyboard, KeyboardStyle, KeyboardResize } from '@capacitor/keyboard'

/**
 * État du clavier et confort de saisie sur mobile.
 *
 * ── Pourquoi on ne redimensionne plus la webview ──────────────────────────
 *
 * L'application tournait en `KeyboardResize.Native`, le seul mode qui touche au
 * CADRE NATIF de la webview. Côté plugin, la remise à pleine hauteur passe par
 * un unique `_updateFrame`, joignable seulement depuis les deux notifications
 * clavier d'iOS et gardé par un `if (self.paddingBottom == height) return;`.
 * Ratée une seule fois, l'écran reste amputé de la hauteur du clavier et RIEN
 * en JavaScript ne peut le réparer : `Keyboard.show()` est `unimplemented` sur
 * iOS, et ni un rechargement ni un changement de route ne touchent un cadre
 * natif. Symptôme observé le 4 septembre 2026, juste après une connexion :
 * la moitié basse de l'écran passe au noir jusqu'à ce qu'on tue l'application.
 *
 * On passe donc en `KeyboardResize.None` : le natif ne bouge plus rien, et le
 * décalage devient une variable CSS, `--keyboard-height`, posée ici. Le pire
 * cas dégénère en « le clavier recouvre un champ », réparable à tout instant.
 *
 * ── D'où vient la valeur ──────────────────────────────────────────────────
 *
 * De la MESURE, pas d'une seule source. `index.html` demande aussi
 * `interactive-widget=resizes-content` : là où le navigateur le comprend, il
 * rétrécit le viewport de mise en page tout seul, et retrancher en plus la
 * hauteur du clavier décalerait deux fois. `keyboardOverlap` compare donc ce
 * que le viewport a déjà rendu à ce que le clavier occupe, et ne réclame que
 * le reste. Sur le web, où le plugin n'existe pas, `visualViewport` suffit.
 *
 * ── Une seule instance qui configure ──────────────────────────────────────
 *
 * Ce composable est monté plusieurs fois en même temps : App.vue pour toute la
 * vie de l'application, plus LoginPage, ResetPasswordPage et
 * ValidateAccountPage. Tout ce qui est global vit donc au niveau du MODULE et
 * n'est fait qu'une fois. Seul `activeInput`, qui désigne le champ de CETTE
 * page, est propre à l'instance.
 *
 * ⚠️ L'ancien `onUnmounted` appelait `Keyboard.removeAllListeners()`, qui n'est
 * pas ciblé : au démontage de LoginPage, c'est-à-dire à chaque connexion
 * réussie, il emportait aussi les écouteurs d'App.vue et le reste de la session
 * tournait avec un état clavier gelé. OtpEmailStep documentait déjà ce piège.
 */

export interface KeyboardOverlapInput {
    /** Hauteur du viewport de mise en page, maintenant (`clientHeight`). */
    layoutHeight: number
    /** La même, mesurée clavier fermé. */
    fullLayoutHeight: number
    /** Hauteur annoncée par le plugin natif, 0 si clavier fermé ou absent. */
    pluginHeight: number
    /** `visualViewport`, quand il existe. */
    visual?: { height: number; offsetTop: number } | null
}

/**
 * Ce que le clavier recouvre ENCORE, une fois retiré ce que le viewport de mise
 * en page a déjà concédé de lui-même. Fonction pure : c'est la seule partie
 * qu'on peut vérifier sans appareil.
 */
export function keyboardOverlap(input: KeyboardOverlapInput): number {
    const { layoutHeight, fullLayoutHeight, pluginHeight, visual } = input

    // Ce que le navigateur a déjà rendu (interactive-widget=resizes-content).
    const alreadyGiven = Math.max(0, fullLayoutHeight - layoutHeight)
    const fromPlugin = Math.max(0, pluginHeight - alreadyGiven)

    // `offsetTop` compte : iOS remonte parfois le viewport visuel au lieu de le
    // rétrécir, et la zone masquée est alors sous la partie déjà décalée.
    const fromVisual = visual
        ? Math.max(0, layoutHeight - (visual.height + visual.offsetTop))
        : 0

    // Le plus grand des deux : chacun rate un cas que l'autre attrape, et une
    // valeur trop petite laisse le clavier manger le champ.
    return Math.round(Math.max(fromPlugin, fromVisual))
}

const isKeyboardVisible = ref(false)
const keyboardHeight = ref(0)

let listeners: PluginListenerHandle[] = []
let setupPromise: Promise<void> | null = null

/** Hauteur du viewport de mise en page mesurée clavier fermé, la référence. */
let fullLayoutHeight = 0
/** Dernière hauteur annoncée par le plugin natif. */
let pluginHeight = 0
/** Le champ visé par le défilement, réévalué à chaque ouverture. */
let activeInputForScroll: HTMLElement | null = null

function scrollActiveInputIntoView() {
    activeInputForScroll?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

/**
 * Pose `--keyboard-height` sur `:root` et l'attribut `data-keyboard`, les deux
 * seules prises que le CSS a sur le clavier.
 */
function applyOverlap() {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const layoutHeight = root.clientHeight

    if (!isKeyboardVisible.value) {
        // Clavier fermé : c'est le moment de (re)prendre la référence, et le
        // seul. La prendre clavier ouvert figerait un viewport déjà rétréci.
        if (layoutHeight > 0) fullLayoutHeight = layoutHeight
        root.style.setProperty('--keyboard-height', '0px')
        root.removeAttribute('data-keyboard')
        return
    }

    const vv = window.visualViewport
    const overlap = keyboardOverlap({
        layoutHeight,
        fullLayoutHeight: fullLayoutHeight || layoutHeight,
        pluginHeight,
        visual: vv ? { height: vv.height, offsetTop: vv.offsetTop } : null,
    })

    root.style.setProperty('--keyboard-height', `${overlap}px`)
    root.setAttribute('data-keyboard', 'open')
}

/**
 * Configuration du plugin, faite UNE fois pour toute l'application. Les
 * écouteurs posés ici ne sont jamais retirés : ils suivent la vie du processus,
 * pas celle d'un composant.
 */
function ensureKeyboardSetup(): Promise<void> {
    if (setupPromise) return setupPromise

    setupPromise = (async () => {
        if (typeof window !== 'undefined') {
            fullLayoutHeight = document.documentElement.clientHeight
            // `visualViewport` est la seule source côté web, et le correctif de
            // dernier recours sur natif si le plugin se tait.
            window.visualViewport?.addEventListener('resize', applyOverlap)
            window.visualViewport?.addEventListener('scroll', applyOverlap)
        }

        try {
            listeners = await Promise.all([
                Keyboard.addListener('keyboardWillShow', (info) => {
                    isKeyboardVisible.value = true
                    pluginHeight = info.keyboardHeight
                    keyboardHeight.value = info.keyboardHeight
                    applyOverlap()
                }),
                Keyboard.addListener('keyboardDidShow', (info) => {
                    isKeyboardVisible.value = true
                    pluginHeight = info.keyboardHeight
                    keyboardHeight.value = info.keyboardHeight
                    applyOverlap()
                    scrollActiveInputIntoView()
                }),
                Keyboard.addListener('keyboardWillHide', () => {
                    isKeyboardVisible.value = false
                    pluginHeight = 0
                    applyOverlap()
                }),
                Keyboard.addListener('keyboardDidHide', () => {
                    isKeyboardVisible.value = false
                    pluginHeight = 0
                    keyboardHeight.value = 0
                    applyOverlap()
                }),
            ])

            await Keyboard.setStyle({ style: KeyboardStyle.Dark })
            // ⚠️ Ne JAMAIS remettre `Native` ici : voir l'en-tête du fichier.
            await Keyboard.setResizeMode({ mode: KeyboardResize.None })
        } catch {
            // Plugin absent sur le web : c'est le cas nominal, pas une erreur.
            // `visualViewport` prend seul le relais.
            listeners = []
        }
    })()

    return setupPromise
}

export function useKeyboardViewport() {
    void ensureKeyboardSetup()

    /** Le champ de CETTE page. Local à l'instance, contrairement au reste. */
    const activeInput = ref<HTMLElement | null>(null)

    const handleInputFocus = (element: HTMLElement) => {
        activeInput.value = element
        activeInputForScroll = element
    }

    const handleInputBlur = () => {
        if (activeInputForScroll === activeInput.value) activeInputForScroll = null
        activeInput.value = null
    }

    const scrollToActiveInput = () => {
        activeInput.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const hideKeyboard = async () => {
        try {
            await Keyboard.hide()
        } catch (error) {
            console.warn('⚠️ Could not hide keyboard:', error)
        }
    }

    const showKeyboard = async () => {
        try {
            await Keyboard.show()
        } catch (error) {
            console.warn('⚠️ Could not show keyboard:', error)
        }
    }

    // On ne retire AUCUN écouteur ici : ils sont globaux et partagés. Seule la
    // référence au champ de cette page est relâchée, pour ne pas retenir un
    // noeud démonté ni faire défiler vers un élément qui n'existe plus.
    onUnmounted(() => {
        if (activeInputForScroll === activeInput.value) activeInputForScroll = null
        activeInput.value = null
    })

    return {
        isKeyboardVisible,
        keyboardHeight,
        activeInput,
        handleInputFocus,
        handleInputBlur,
        hideKeyboard,
        showKeyboard,
        getKeyboardHeight: () => keyboardHeight.value,
        getKeyboardVisibility: () => isKeyboardVisible.value,
        scrollToActiveInput,
    }
}

/** Réservé aux tests : remet le module dans son état initial. */
export function __resetKeyboardViewportForTests() {
    listeners.forEach((h) => void h.remove())
    listeners = []
    setupPromise = null
    activeInputForScroll = null
    fullLayoutHeight = 0
    pluginHeight = 0
    isKeyboardVisible.value = false
    keyboardHeight.value = 0
}
